import React, {
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useState,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/root-reducer';
import { UserInfoResponse } from '../types/types';

interface PermissionContextType {
  /** Direct assignments (permission_user) — used for route/page access */
  permissions: string[];
  /** Role template permissions (informational; not used alone for route access) */
  rolePermissions: string[];
  roles: string[];
  menuModelKeys: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string | string[]) => boolean;
  hasMenuModel: (key: string) => boolean;
  /** Menu model on role/user AND direct permission required */
  canAccessMenuRoute: (
    menuModelKey: string,
    permissionNames: string[],
    requireAll?: boolean
  ) => boolean;
  canAccess: (resource: string, action: string) => boolean;
  isLoading: boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { userInfo } = useSelector((state: RootState) => state.setReducer);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [menuModelKeys, setMenuModelKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadPermissions = useCallback(() => {
    const userInfoTyped = userInfo as UserInfoResponse | null;
    if (userInfoTyped?.user) {
      const user = userInfoTyped.user;
      const userPermissionsRaw = user.permissions || [];
      const userRoles = user.roles || [];

      // Handle permissions as either array of strings or array of objects
      // Extract permission names if they're objects
      const userPermissions: string[] = userPermissionsRaw.map(
        (perm: string | { name: string }) => {
          if (typeof perm === 'string') {
            return perm;
          }
          // If it's an object, extract the name field
          return perm.name || '';
        }
      );

      setPermissions(userPermissions);
      setRolePermissions((user.role_permissions || []).filter(Boolean));
      setRoles(userRoles);
      setMenuModelKeys(user.menu_model_keys || []);
      setIsLoading(false);
    } else {
      setPermissions([]);
      setRolePermissions([]);
      setRoles([]);
      setMenuModelKeys([]);
      setIsLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const refreshPermissions = async () => {
    // This will be called when permissions are updated
    // The App.tsx will reload user data which will update Redux
    loadPermissions();
  };

  // Helper to check if user is Super Admin (defined first to avoid circular dependency)
  const isSuperAdmin = (): boolean => {
    if (!roles || roles.length === 0) return false;
    return roles.includes('superadministrator');
  };

  const hasRole = (role: string | string[]): boolean => {
    if (!role) return false;
    const roleArray = Array.isArray(role) ? role : [role];
    return roleArray.some((r) => roles.includes(r));
  };

  const hasMenuModel = (key: string): boolean => {
    if (!key) return false;
    if (isSuperAdmin()) return true;
    return menuModelKeys.includes(key);
  };

  const hasPermission = (permission: string): boolean => {
    if (!permission) return false;
    if (isSuperAdmin()) {
      return true;
    }
    // Route and UI gates use direct user permissions only (permission_user)
    return permissions.includes(permission);
  };

  const canAccessMenuRoute = (
    menuModelKey: string,
    permissionNames: string[],
    requireAll = false
  ): boolean => {
    if (!menuModelKey || !permissionNames?.length) {
      return false;
    }
    if (isSuperAdmin()) {
      return true;
    }
    if (!hasMenuModel(menuModelKey)) {
      return false;
    }
    return requireAll
      ? permissionNames.every((p) => hasPermission(p))
      : permissionNames.some((p) => hasPermission(p));
  };

  const hasAnyPermission = (permissionList: string[]): boolean => {
    if (!permissionList || permissionList.length === 0) return false;

    // Only Super Admin always has all permissions
    // Administrator permissions are filtered by backend based on model assignments
    if (isSuperAdmin()) {
      return true;
    }
    return permissionList.some((permission) => hasPermission(permission));
  };

  const hasAllPermissions = (permissionList: string[]): boolean => {
    if (!permissionList || permissionList.length === 0) return false;

    // Only Super Admin always has all permissions
    // Administrator permissions are filtered by backend based on model assignments
    if (isSuperAdmin()) {
      return true;
    }
    return permissionList.every((permission) => hasPermission(permission));
  };

  const canAccess = (resource: string, action: string): boolean => {
    // Only Super Admin always has access
    // Administrator permissions are filtered by backend based on model assignments
    if (isSuperAdmin()) {
      return true;
    }
    // Try both formats: new format (resource.action) and old format (action_resource)
    const newFormatPermission = `${resource}.${action}`;
    const oldFormatPermission = `${action}_${resource}`;
    return hasPermission(newFormatPermission) || hasPermission(oldFormatPermission);
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        rolePermissions,
        roles,
        menuModelKeys,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasRole,
        hasMenuModel,
        canAccessMenuRoute,
        canAccess,
        isLoading,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Convenience hook for checking a single permission
export const usePermission = (permission: string): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
};
